import React, { useEffect, useState } from "react";
import LessonNavBar from "components/Navbars/LessonNavBar.js";
import LessonFooterNav from "components/NavigationButton/LessonFooterNav.js";
import PromptLabHubComponent from "components/PromptLabHub/PromptLabHubComponent.js";
import { getV2LessonMetaByRoute, getNextV2LessonRoute } from "pages/claudeDevV2Course/devV2LessonsRegistry.js";

// The lesson page for "Claude AI Developer V2" (CD02) Module 6, driven by the registry.
//
// CD02's first 38 lessons each ship a hand-written component file — verified
// byte-for-byte identical apart from a route string — and they stay that way: this
// component was added for Module 6, and rewiring a live course's routing is a much
// bigger change than adding a module. Nothing in those files is per-lesson, though;
// the layout (promptLab / canvas / template / webLab) is chosen at runtime by
// PromptLabHubComponent from `lessonMeta.layout`. So Module 6's five lessons render
// through this one component instead, and anything added to CD02 later should too.
//
// CD03 does the same thing for all 36 of its lessons — see
// pages/claudeDevV3Course/V3LessonPage.js, which this mirrors.

const V2LessonPageInner = ({ route, setAiPageContext, setCurrentCode, setCurrentOuput }) => {
    const meta = getV2LessonMetaByRoute(route);
    const nextRoute = getNextV2LessonRoute(route);
    const [allCompleted, setAllCompleted] = useState(false);

    useEffect(() => {
        setAiPageContext?.("claudeCourseLesson");
    }, [setAiPageContext]);

    // The navbar sits before <main>, not inside it, so the header is not part
    // of the main landmark. The wrapper keeps the old full-height background.
    return (
        <div className="lessonBackgroundColor1 lessonMain" style={{ minHeight: "100vh" }}>
            <LessonNavBar />
            <main id="main" tabIndex={-1} className="lessonMain">
                <section>
                    <PromptLabHubComponent
                        lessonMeta={meta}
                        setallCheckboxesCompleted={setAllCompleted}
                        setCurrentCode={setCurrentCode}
                        setCurrentOuput={setCurrentOuput}
                    />
                </section>
                <LessonFooterNav route={route} nextRoute={nextRoute} showNext={allCompleted} />
            </main>
        </div>
    );
};

// `key={route}` is load-bearing, not cosmetic.
//
// With one component per lesson — which is how CD02's other 38 lessons are wired —
// navigating Lesson 1 -> Lesson 2 swaps one component TYPE for another, so React
// unmounts and remounts and every lesson starts clean. Sharing a single component
// removes that: React sees the same type in the same position and reconciles
// instead, keeping the old instance and its state alive.
//
// That breaks PromptLabHubComponent specifically. Its "load or generate the lesson
// definition" effect is guarded by `didInitRef`, a ref set to true on first run and
// never reset, so a reused instance would never fetch the new lesson — the student
// would click Next and keep reading the previous lesson. Local state here
// (`allCompleted`) would be just as stale.
//
// Keying an inner component on the route restores the remount, and doing it here
// rather than at the <Route> keeps the guarantee with the component: no caller can
// wire Module 6's routes in a way that loses it.
const V2LessonPage = (props) => <V2LessonPageInner key={props.route} {...props} />;

export default V2LessonPage;
